import type { AlgoAction, TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';

export interface TradeDetailRecord {
    detailType?: string;
    detailJson?: Array<{ [key: string]: any }>;
}

export const findDetailJson = <T>(details: TradeDetailRecord[], type: TradePlanDetailType): T[] =>
    (details.find((d) => d.detailType === type)?.detailJson as T[]) ?? [];

/** The single record of a one-row-per-account detail (folded by p_id, so its JSON is a one-element array). */
export const findDetailRecord = <T>(details: TradeDetailRecord[], type: TradePlanDetailType): T | undefined =>
    findDetailJson<T>(details, type)[0];

export interface BondLadderTradePlanBuyDetails {
    pId: string;
    finalBudget?: number;
    allocationType?: string;
    sId?: string;
    cusip?: string;
    status?: string;
    contractMultiplier?: number;
    priceMultiplier?: number;
    askPrice?: number;
    bidPrice?: number;
    priceTimestamp?: string;
    priceError?: string;
    priceSource?: string;
    accruedInterest?: number;
    dirtyPrice?: number;
    faceValue?: number;
    calculatedQuantity?: number;
    quantity?: number;
}

export interface BondLadderTradePlanSellDetails {
    pId: string;
    sId?: string;
    symbol?: string;
    positionQuantity?: number;
    priceMultiplier?: number;
    contractMultiplier?: number;
    maturityDate?: string;
    currentExposure?: number;
    askPrice?: number;
    bidPrice?: number;
    priceTimestamp?: string;
    priceSource?: string;
    accruedInterest?: number;
    dirtyPrice?: number;
    allocationType?: string;
    finalBudget?: number;
    grossBudget?: number;
    exposureCumsum?: number;
    budgetMinusExposureCumsum?: number;
    netTradeBudget?: number;
    realTradeBudget?: number;
    quantityCalculated?: number;
    quantity?: number;
}

export interface DirectStocksBuyTradePlanPreferences {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    factorCombination?: string;
    sId?: string;
    scId: string;
    symbol?: string;
    quantity?: number;
    marketValue?: number;
    benchmarkWeight?: number;
    extendedMarketWeight?: number;
    passiveRank?: number;
    factorRank?: number;
    reason?: string;
    side?: string;
    universeCategory?: string;
    targetEquityValueUsa?: number;
    numFactors?: number;
    isExtendedMarket?: boolean;
    factorActiveValue?: number;
    minBuyThreshold?: number;
    minBuyValue?: number;
    targetWeight?: number;
    targetBenchmarkValue?: number;
    activeBenchmarkValue?: number;
    underweightValue?: number;
    activeValue?: number;
    stockPreferenceCategory?: string;
    factorWinnerValue?: number;
    preferencesRank?: number;
}

export interface DirectStocksBuyTradePlanTradeInput {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    sId?: string;
    scId: string;
    symbol?: string;
    reason?: string;
    activeValue?: number;
    bidPrice?: number;
    askPrice?: number;
    quotePrice?: number;
    priceSource?: string;
    priceTimestamp?: string;
    price?: number;
    maxBuyAllowance?: number;
    minBuyValue?: number;
    activeQuantity?: number;
    expMaxTradeQuantity?: number;
    prevTradeQuantity?: number;
    cappedDesiredTradeQuantity?: number;
    desiredTradeValue?: number;
    cumSumDesiredTradeValue?: number;
    availableAllowance?: number;
    tradeValue?: number;
    action?: string;
    tradeQuantity?: number;
    tradeInputRank?: number;
}

export interface DirectStocksBuyTradePlanUniverse {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    factorCombination?: string;
    sId?: string;
    scId?: string;
    symbol?: string;
    quantity?: number;
    marketValue?: number;
    benchmarkWeight?: number;
    extendedMarketWeight?: number;
    passiveRank?: number;
    factorRank?: number;
}

export interface DirectStocksSellTradePlanPreferences {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    factorCombination?: string;
    sId?: string;
    scId: string;
    symbol?: string;
    quantity?: number;
    marketValue?: number;
    benchmarkWeight?: number;
    extendedMarketWeight?: number;
    passiveRank?: number;
    factorRank?: number;
    unrealizedReturnLow?: number;
    tlhSharesRatioLow?: number;
    unrealizedReturnHigh?: number;
    tlhSharesRatioHigh?: number;
    reason?: string;
    side?: string;
    universeCategory?: string;
    targetEquityValueUsa?: number;
    numFactors?: number;
    isExtendedMarket?: boolean;
    factorWinnerThreshold?: number;
    factorActiveValue?: number;
    tlhActiveValue?: number;
    minSellThreshold?: number;
    minSellValue?: number;
    minBuyValue?: number;
    tlhLossThresholdLow?: number;
    tlhLossThresholdHigh?: number;
    targetWeight?: number;
    targetBenchmarkValue?: number;
    activeRatio?: number;
    stockPreferenceCategory?: string;
    activeSellValue?: number;
    overweightValue?: number;
    tlhValueHigh?: number;
    factorLoserValue?: number;
    sellUniverseLen?: number;
    outOfRankThreshold?: number;
    tlhValueLow?: number;
    factorWeakNonLoserValue?: number;
    preferencesRank?: number;
}

export interface DirectStocksSellTradePlanTradeInput {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    sId?: string;
    scId: string;
    symbol?: string;
    reason?: string;
    stockPreferenceCategory?: string;
    activeRatio?: number;
    currentQuantity?: number;
    bidPrice?: number;
    askPrice?: number;
    quotePrice?: number;
    priceSource?: string;
    priceTimestamp?: string;
    price?: number;
    minSellAllowance?: number;
    maxSellAllowance?: number;
    activeQuantity?: number;
    expMaxTradeQuantity?: number;
    prevTradeQuantity?: number;
    cappedDesiredTradeQuantity?: number;
    desiredTradeValue?: number;
    selectedAllowance?: number;
    cumSumDesiredTradeValue?: number;
    availableAllowance?: number;
    tradeValue?: number;
    action?: string;
    tradeQuantity?: number;
    tradeInputRank?: number;
}

export interface DirectStocksSellTradePlanUniverse {
    optimizationId?: number;
    algoId?: number;
    pId: string;
    factorCombination?: string;
    sId?: string;
    scId?: string;
    symbol?: string;
    quantity?: number;
    marketValue?: number;
    benchmarkWeight?: number;
    extendedMarketWeight?: number;
    passiveRank?: number;
    factorRank?: number;
    unrealizedReturnLow?: number;
    tlhSharesRatioLow?: number;
    unrealizedReturnHigh?: number;
    tlhSharesRatioHigh?: number;
}

export interface DirectStocksTradePlanPortfolioConfig {
    optimizationId?: number;
    algoId?: number;
    tradeDate?: string;
    pId: string;
    aC?: string;
    portfolioGuid?: string;
    accountAlias?: string;
    nav?: number;
    cash?: number;
    isTaxable?: boolean;
    ndvrPortfolioType?: string;
    isTaxLossHarvesting?: boolean;
    isDirectStock?: boolean;
    brokerId?: string;
    targetGrowthSleeve?: number;
    targetEquityWeightUs?: number;
    isLevered?: boolean;
    isExtendedMarket?: boolean;
    isFactorMomentum?: boolean;
    isFactorValue?: boolean;
    isFactorLowVolatility?: boolean;
    isFactorQuality?: boolean;
    numFactors?: string;
    tlhActiveRatio?: number;
    tlhLossThresholdRatio?: number;
    minActiveValue?: number;
    minSizeValue?: number;
    minBuyRatio?: number;
    minSellRatio?: number;
    minSellRatioExtMkt?: number;
    minSellValueExtMkt?: number;
    factorWinnerThreshold?: string;
    factorActiveRatio?: number;
    activeWeightDivisor?: number;
    cashAllocationRatio?: number;
    maxSellTurnoverRatio?: number;
    tlhLossThresholdLow?: number;
    tlhLossThresholdHigh?: number;
    targetEquityValueUsa?: number;
    currentEquityValueUsa?: number;
    activeEquityValueUsa?: number;
    maxBuyAllowance?: number;
    minSellAllowance?: number;
    maxSellAllowance?: number;
    factorActiveValue?: number;
    tlhActiveValue?: number;
    minSellValue?: number;
    minBuyValue?: number;
    minSellThreshold?: number;
    factorCombination?: string;
    reviewStatus?: string;
    isSuspended?: boolean;
    isOnboarded?: boolean;
    isMirrorLinked?: boolean;
}

export interface GlobalBetaBuyTradePlanDetails {
    pId: string;
    sId?: string;
    allocationType?: string;
    minSellConstraint?: number;
    maxBuyConstraint?: number;
    buy?: boolean;
    tradeQuantity?: number;
    tradeAmount?: number;
    price?: number;
    buyRank?: number;
    tradable?: boolean;
    notTradableReason?: string;
    targetExposure?: number;
    preference?: number;
    clientDoNotBuy?: boolean;
    washSaleDoNotBuy?: boolean;
    existingPositionMarketValue?: number;
    hasPosition?: boolean;
    bidPrice?: number;
    askPrice?: number;
    quotePrice?: number;
    priceTimestamp?: string;
    priceSource?: string;
    isPriceMissing?: boolean;
    minBuyAmount?: number;
    minBuyMultiplier?: number;
    minBuySize?: number;
    isAboveMinBuySize?: boolean;
}

export interface GlobalBetaSellTradePlanDetails {
    pId: string;
    sId?: string;
    openLotId?: string;
    allocationType?: string;
    minSellConstraint?: number;
    maxBuyConstraint?: number;
    tradeQuantity?: number;
    tradeAmount?: number;
    price?: number;
    tradable?: boolean;
    notTradableReason?: string;
    targetExposure?: number;
    clientDoNotSell?: boolean;
    washSaleLastToSell?: boolean;
    lotQuantity?: number;
    existingPositionMarketValue?: number;
    costBasis?: number;
    lossRatio?: number;
    hasLoss?: boolean;
    lastToSell?: boolean;
    bidPrice?: number;
    askPrice?: number;
    quotePrice?: number;
    priceTimestamp?: string;
    priceSource?: string;
    isPriceMissing?: boolean;
    minSellAmount?: number;
    minSellMultiplier?: number;
    minSellSize?: number;
    isAboveMinSellSize?: boolean;
}

export interface GlobalBetaTradableLots {
    pId: string;
    sId?: string;
    openLotId?: string;
    allocationType?: string;
    sellLot?: string;
    postSellCumSum?: number;
    sumPreSell?: number;
    gapToFill?: number;
    amountToSell?: number;
    quantity?: number;
    tradeQuantity?: number;
    lossRatio?: number;
    lastToSell?: boolean;
    tradeAmount?: number;
    existingPositionMarketValue?: number;
    price?: number;
    lotQuantity?: number;
}

export interface PaydayTradePlanCashflow {
    optimizationId: number;
    algoId: number;
    investmentAlgo: string;
    algoAction: AlgoAction;
    tradeDate: string;
    rowId?: number;
    loadId?: number;
    pId: string;
    portfolioGuid?: string;
    portfolioTargetEndDate?: string;
    portfolioTaxType?: string;
    withdrawalTaxRate?: number;
    manuallySetWithdrawalTaxRate?: boolean;
    factorGuid?: string;
    cashflowType: string;
    currency?: string;
    factorStartDate?: string;
    frequencyType?: string;
    startWeekDay?: string;
    factorEndDate?: string;
    occurrenceCount?: number;
    yearlyInflationRate?: number;
    inflationStartDate?: string;
    cashflowDate: string;
    cashflowUnadjustedAmount?: number;
    cashflowInflationMultiplier?: number;
    cashflowInflatedAmount?: number;
    cashflowGrossInflatedAmount?: number;
    cashflowFinalAmount: number;
    cashFlowMonth: number;
    algoConfigEndDate?: string;
    active?: boolean;
    createdAt?: string;
}

export interface PaydayTradePlanPortfolio {
    optimizationId: number;
    algoId: number;
    investmentAlgo: string;
    algoAction: AlgoAction;
    pId: string;
    portfolioGuid?: string;
    currentPv?: number;
    targetPv?: number;
    nav?: number;
    cash?: number;
    cashFuturesMargin?: number;
    bufferAmount?: number;
    feesAmount?: number;
    tradeQuantitySum?: number;
    initialDuration?: number;
    initialFundedness?: number;
    duration?: number;
    fundedness?: number;
    previousTargetSecuredness?: number;
    targetCfiSecuredness?: number;
    cfiAmount?: number;
    paydayAmount?: number;
    initialSecuredness?: number;
}

export interface PaydayTradePlanPosition {
    optimizationId: number;
    algoId: number;
    investmentAlgo: string;
    algoAction: AlgoAction;
    pId: string;
    portfolioGuid?: string;
    sId: string;
    symbol?: string;
    description?: string;
    priceMultiplier?: number;
    contractMultiplier?: number;
    targetPv?: number;
    quotePrice?: number;
    priceSource?: string;
    priceTimestamp?: string;
    currentQuantity?: number;
    currentPv?: number;
    accruedInterestShifted?: number;
    dirtyPrice?: number;
    targetQuantity?: number;
    tradeQuantity?: number;
    tradeQuantityRounded?: number;
    createdAt?: string;
}

export interface PaydayTradePlanPV {
    optimizationId: number;
    algoId: number;
    investmentAlgo: string;
    algoAction: AlgoAction;
    cashFlowMonth?: number;
    pId: string;
    portfolioGuid?: string;
    cashflowFinalAmount?: number;
    sId?: string;
    symbol?: string;
    description?: string;
    pvMultiplier?: number;
    targetCfiSecuredness?: number;
    pvMultiplierSecured?: number;
    pvMultiplierCash?: number;
    pvMultiplierCfm?: number;
    targetPv?: number;
    createdAt?: string;
}

export interface ConvictionDebug {
    pId: string;
    sId?: string;
    allocationType?: string;
    targetWeight?: number;
    nav?: number;
    targetExposure?: number;
    quotePrice?: number;
    targetQuantity?: number;
    currentQuantity?: number;
    tradeQuantity?: number;
}

export interface CustomAssetDebug {
    pId: string;
    sId?: string;
    allocationType?: string;
    targetWeight?: number;
    sumTargetWeight?: number;
    normalizedTargetWeight?: number;
    sumTargetExposure?: number;
    originalTargetExposure?: number;
    currentExposure?: number;
    buyRestriction?: string;
    sellRestriction?: string;
    replacementAwareTargetExposure?: number;
    restrictionAwareTargetExposure?: number;
    buyConstrainedTargetExposure?: number;
    originalActiveExposure?: number;
    harvestableExposure?: number;
    tlhActiveExposure?: number;
    sellConstrainedActiveExposure?: number;
    bidPrice?: number;
    askPrice?: number;
    orderPrice?: number;
    assetClass?: string;
    accruedInterest?: number;
    priceSource?: string;
    priceMultiplier?: number;
    contractMultiplier?: number;
    quantity?: number;
    normalizedCurrentQuantity?: number;
    sizingPrice?: number;
    tradeQuantityContinuous?: number;
    tradeQuantity?: number;
    isActive?: boolean;
}

export interface ReplacementSolverDebug {
    pId: string;
    allocationType?: string;
    tailSId?: string;
    headSId?: string;
    tailSupply?: number;
    headSupply?: number;
    flow?: number;
    capacity?: number;
    unitCost?: number;
}

export interface RestrictionDetail {
    pId: string;
    sId?: string;
    allocationType?: string;
    currentExposure?: number;
    targetExposure?: number;
    buyRestriction?: string;
    sellRestriction?: string;
    restrictedTargetExposure?: number;
    targetExposureShortfall?: number;
    replacementTargetExposure?: number;
    replacementAdjustedTargetExposure?: number;
    sumUnrestrictedTargetExposure?: number;
    sumReplacementAdjustedTargetExposure?: number;
    netTargetExposureShortfall?: number;
    eligibleTargetExposure?: number;
    sumEligibleTargetExposure?: number;
    redistributionWeight?: number;
    restrictionAwareTargetExposure?: number;
}

export interface BuyConstraintDebug {
    pId: string;
    sId?: string;
    allocationType?: string;
    currentExposure?: number;
    targetExposure?: number;
    maxBuyConstraint?: number;
    currentExposurePercent?: number;
    minExposurePercent?: number;
    nextExposurePercent?: number;
    cumsumCurrentExposure?: number;
    cumsumTargetExposure?: number;
    cumsumExposureToNext?: number;
    commonFillPercent?: number;
    sumExposureToFill?: number;
    inFillGroup?: boolean;
    sumFilledTargetExposure?: number;
    excessExposure?: number;
    excessFillPercent?: number;
    finalFillPercent?: number;
    buyConstrainedTargetExposure?: number;
}

export interface SellConstraintOpenLot {
    pId: string;
    sId?: string;
    allocationType?: string;
    openLotId?: string;
    accountType?: string;
    currentPositionExposure?: number;
    originalActivePositionExposure?: number;
    tlhActivePositionExposure?: number;
    currentExposure?: number;
    lotQuantity?: number;
    costBasis?: number;
    costBasisPerShare?: number;
    hifoCumsumLotExposure?: number;
    hifoCumsumPriorLotExposure?: number;
    activeLotExposure?: number;
    isLongTerm?: boolean;
    longTermCapGainsTaxRate?: number;
    shortTermCapGainsTaxRate?: number;
    ownTermTaxRate?: number;
    crossTermTaxRate?: number;
    unrealizedPnl?: number;
    effectiveUnrealizedPnl?: number;
    targetPositionExposure?: number;
    meanPositionExposure?: number;
    normalizedActiveRatio?: number;
    scaledActiveRatio?: number;
    activeRatioMultiplier?: number;
    taxCost?: number;
    unitTaxCost?: number;
    tier?: number;
    score?: number;
    cumminScore?: number;
    sellRatio?: number;
    stPnlRealizedYtd?: number;
    ltPnlRealizedYtd?: number;
    activePnl?: number;
    activeGains?: number;
    activeStPnl?: number;
    activeLtPnl?: number;
    cumsumStPnl?: number;
    cumsumLtPnl?: number;
    cumsumPriorStPnl?: number;
    cumsumPriorLtPnl?: number;
    cumsumPriorOwnTermPnl?: number;
    cumsumPriorCrossTermPnl?: number;
    priorNetLosses?: number;
    priorCrossNettedOwnTermLosses?: number;
    lossCoveredGains?: number;
    crossTermGains?: number;
    ownTermGains?: number;
    crossTermTax?: number;
    ownTermTax?: number;
    dominantTermTaxRate?: number;
    totalTax?: number;
    taxBudget?: number;
    taxBudgetYear?: number;
    nav?: number;
    effectiveTaxBudget?: number;
    taxBudgetShortfall?: number;
    lotTaxBudget?: number;
    coveredOwnTermGainsRatio?: number;
    coveredCrossTermGainsRatio?: number;
    coveredGains?: number;
    coveredTaxRatio?: number;
    minSellConstraint?: number;
    isCappedAtMinSell?: boolean;
    cumsumTaxConstrainedLotExposure?: number;
    minSellExcessExposure?: number;
    minSellExposure?: number;
    sellRestriction?: string;
    lastToSellRatio?: number;
    harvestableUnderweightLotExposure?: number;
    cumsumHarvestableUnderweightLotExposure?: number;
    tlhBudget?: number;
    tlhBudgetShortfall?: number;
    tlhBudgetCoveredLotExposure?: number;
    tlhSellRatio?: number;
    constrainedSellRatio?: number;
    sellConstrainedActiveExposure?: number;
}

export interface SellConstraintClosedLot {
    pId: string;
    sId?: string;
    allocationType?: string;
    closedLotId?: string;
    isLongTerm?: boolean;
    pnlRealized?: number;
    stPnlRealized?: number;
    ltPnlRealized?: number;
    tradeDate?: string;
}

export interface TradeQuantityDetail {
    pId: string;
    sId?: string;
    allocationType?: string;
    sizingPrice?: number;
    tradeQuantityContinuous?: number;
    tradeQuantityInteger?: number;
    targetQuantityContinuous?: number;
    overweightRatio?: number;
    underweightRatio?: number;
    fractionalBuyQuantity?: number;
    fractionalBuyExposure?: number;
    truncatedSellExposure?: number;
    plusOneBudget?: number;
    plusOneBudgetRemaining?: number;
    plusOneApplied?: boolean;
    minSellConstraint?: number;
    realizedSellExposure?: number;
    minSellShortfall?: number;
    tradeQuantity?: number;
}
